import axios, { isAxiosError } from 'axios';

interface TaskRequester {
  email: string;
  name: string;
}

interface CreateTaskParams {
  subject: string;
  description: string;
  priority?: 'low' | 'medium' | 'high';
  parent_organization_id: number;
  secondary_organization_id?: number;
  chat_session_id?: string;
  topic_id?: number;
  requester?: TaskRequester;
}

interface TaskResponse {
  id: number;
  subject: string;
  description: string;
  priority: string;
  status: string;
  task_number: string;
  parent_organization_id: number;
  secondary_organization_id?: number;
  created_by_id?: number;
  assigned_to_id?: number;
  requester?: {
    id: number;
    email: string;
    name: string;
    created_at: string;
    updated_at: string;
  };
  closed_at?: string;
  resolved_at?: string;
  created_at: string;
  updated_at: string;
  topic: {
    id: number;
    name: string;
  };
}

interface CreateTaskResponse {
  task: TaskResponse;
}

interface CreateTaskError {
  errors: string[];
}

/**
 * Creates a support task via the public tasks API.
 *
 * @param endpoint - Base URL for the writable API
 * @param taskData - Task information including subject, description, organization IDs, and requester details
 * @param recaptcha_token - Optional ReCaptcha v3 token for request authentication
 * @returns Promise resolving to the created task information
 * @throws Error if the API request fails or returns invalid data
 */
export async function createTask({
  endpoint,
  taskData,
  recaptcha_token,
}: {
  endpoint: string;
  recaptcha_token?: string;
  taskData: CreateTaskParams;
}): Promise<TaskResponse> {
  try {
    const taskUrl = new URL('/api/v4/task_management/public/tasks', endpoint).toString();

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
    };

    if (recaptcha_token) {
      headers['X-Recaptcha-Token'] = recaptcha_token;
    }

    const { data } = await axios.post<CreateTaskResponse>(taskUrl, {
      ...taskData,
      source: 'ai_chat'
    }, { headers });

    const task = data.task;

    if (!task || !task.id || !task.task_number) {
      throw new Error('Invalid response format from task API');
    }

    return task;
  } catch (e) {
    if (isAxiosError(e) && e.response?.data?.errors) {
      const errors = e.response.data.errors;
      throw new Error(`Task creation failed: ${Array.isArray(errors) ? errors.join(', ') : errors}`);
    }

    throw new Error(`Task creation failed: ${e instanceof Error ? e.message : 'Unknown error'}`);
  }
}
